import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { CurrencyPipe } from '@frontend/sports/common/core/feature/money';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { LoadingIndicatorModule } from '@frontend/sports/loading-indicator-feature';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsDivider } from '@frontend/ui/divider';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { DynamicLayoutSlotComponent } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { NgxPopperjsModule } from 'ngx-popperjs';

import { ArcModule } from '../arc/arc.module';
import { RaceViewBestOddsBadgeComponent } from '../best-odds-guarantee/race-view-best-odds-badge.component';
import { SGPBadgeComponent } from '../betbuilder/components/badge/sgp-badge/sgp-badge.component';
import { SGPPlusBadgeComponent } from '../betbuilder/components/badge/sgp-plus-badge/sgp-plus-badge.component';
import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { EpcotNotificationComponent } from '../common/epcotNotifications/notification.component';
import { CrmPostbetSignpostingModule } from '../crm-postbet-signposting/crm-postbet-signposting.module';
import { DirectivesModule } from '../directives/directives.module';
import { HelpBoxBadgeComponent } from '../help-box/help-box-badge.component';
import { HelpBoxComponent } from '../help-box/help-box.component';
import { MyBetsBaseModule } from '../my-bets-base/my-bets-base.module';
import { NativeAlertsModule } from '../native-alerts/native-alerts.module';
import { NavigationCoreModule } from '../navigation-core/navigation-core.module';
import { NumpadModule } from '../numpad/numpad.module';
import { BootstrapQuickLink } from '../quick-links/quick-links.bootstrap';
import { ShareMyBetModule } from '../share-my-bet/share-my-bet.module';
import { PriceboostCountSignpostComponent } from '../shared-priceboost/Components/priceboost-count-signpost.component';
import { MyBetsSubNavigationComponent } from '../sub-navigation/mybets-sub-navigation/my-bets-sub-navigation.component';
import { TimerModule } from '../timers/timer.module';
import { MyBetsAutoCashoutBadgeComponent } from './components/badges/auto-cashout-badge.component';
import { MyBetsBetSlipBadgesComponent } from './components/badges/my-bets-betslip-badges.component';
import { MyBetsEditedBadgeComponent } from './components/badges/my-bets-edited-badge.component';
import { MyBetsOutcomeBadgeComponent } from './components/badges/outcome-badge.component';
import { MyBetsBetbuilderCardComponent } from './components/bet-builder/my-bets-betbuilder-card.component';
import { MyBetsBetbuilderExpandedInfoComponent } from './components/bet-builder/my-bets-betbuilder-expanded-info.component';
import { MyBetBetslipCardTitleOptionsComponent } from './components/betslip-card-title-options/my-bets-betslip-card-title-options.component';
import { MyBetsBetslipEventInfoComponent } from './components/betslip-card/my-bets-betslip-event-info.component';
import { MyBetsBetslipExpandComponent } from './components/betslip-card/my-bets-betslip-expand.component';
import { MyBetsBetslipMarketNameComponent } from './components/betslip-card/my-bets-betslip-market-name.component';
import { MyBetsBetslipNonEventInfoComponent } from './components/betslip-card/my-bets-betslip-non-event-info.component';
import { MyBetsBetslipOptionNameComponent } from './components/betslip-card/my-bets-betslip-option-name.component';
import { MyBetsBetslipPayoutInfoComponent } from './components/betslip-card/my-bets-betslip-payout-info.component';
import { MyBetsBetslipSportCompetitionNameComponent } from './components/betslip-card/my-bets-betslip-sport-competition-name.component';
import { MyBetsBetslipComponent } from './components/betslip-card/my-bets-betslip.component';
import { MyBetsUsedRewardTokenEpcotComponent } from './components/betslip-card/my-bets-used-reward-token-epcot.component';
import { MyBetsUsedRewardTokenComponent } from './components/betslip-card/my-bets-used-reward-token.component';
import { MyBetsEmptyListComponent } from './components/betslip-list-empty/my-bets-empty-list.component';
import { MyBetsColumnsService } from './components/betslip-list/my-bets-columns.service';
import { MyBetsListColumnComponent } from './components/betslip-list/my-bets-list-column.component';
import { MyBetsListTrackingService } from './components/betslip-list/my-bets-list-tracking.service';
import { MyBetsListComponent } from './components/betslip-list/my-bets-list.component';
import { MyBetsListService } from './components/betslip-list/my-bets-list.service';
import MyBetsDetailsSubNavigationService from './components/details/my-bets-details-subnavigation.service';
import MyBetsDetailsComponent from './components/details/my-bets-details.component';
import MyBetsDetailsGuard from './components/details/my-bets-details.guard';
import MyBetsDetailsResolver from './components/details/my-bets-details.resolver';
import { MyBetsEarlyPayoutAutocashoutButtonComponent } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-autocashout-button.component';
import { MyBetsEarlyPayoutAutocashoutValidationService } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-autocashout-validation.service';
import { MyBetsEarlyPayoutAutocashoutComponent } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-autocashout.component';
import { MyBetsEarlyPayoutPopperTabsComponent } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-popper-tabs.component';
import { MyBetsEarlyPayoutRemoveAutocashoutButtonComponent } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-remove-autocashout-button.component';
import { MyBetsEarlyPayoutRemoveAutocashoutComponent } from './components/earlypayout/auto-cash-out/my-bets-earlypayout-remove-autocashout.component';
import { MyBetsEarlyPayoutButtonComponent } from './components/earlypayout/my-bets-earlypayout-button.component';
import { MyBetsEarlyPayoutContainerComponent } from './components/earlypayout/my-bets-earlypayout-container.component';
import { MyBetsEarlyPayoutSettingsComponent } from './components/earlypayout/my-bets-earlypayout-settings.component';
import { MyBetsEditBetButtonComponent } from './components/edit-bet/my-bets-edit-bet-button.component';
import { MyBetsEditBetHistoryBetslipComponent } from './components/edit-bet/my-bets-edit-bet-history-betslip.component';
import { MyBetsEditBetHistoryHeaderComponent } from './components/edit-bet/my-bets-edit-bet-history-header.component';
import { MyBetsEditBetHistoryListComponent } from './components/edit-bet/my-bets-edit-bet-history-list.component';
import { MyBetsEditingOverlayComponent } from './components/edit-bet/my-bets-editing-overlay.component';
import { MyBetsFilterButtonComponent } from './components/filters/my-bets-filter-button.component';
import { MyBetsFilterConstraintButtonComponent } from './components/filters/my-bets-filter-constraint-button.component';
import { MyBetsFilterComponent } from './components/filters/my-bets-filter.component';
import { MyBetsSettledFiltersComponent } from './components/filters/my-bets-settled-filters.component';
import { HelpBoxArchivedComponent } from './components/help-box-archived-bets/help-box-archived-bets.component';
import { HelpBoxReBetComponent } from './components/help-box-re-bet/help-box-re-bet.component';
import { HelpBoxTransactionComponent } from './components/help-box-transaction-bets/help-box-transaction-bets.component';
import { EditBetCashoutMessageComponent } from './components/messages/edit-bet-cashout-message.component';
import { MyBetsBetbuilderTitleComponent } from './components/multi-bet-card/my-bets-multi-bet-betbuilder-title.component';
import { MyBetsMultiBetCardCollapsedComponent } from './components/multi-bet-card/my-bets-multi-bet-card-collapsed.component';
import { MyBetsMultiBetCardDetailComponent } from './components/multi-bet-card/my-bets-multi-bet-card-detail.component';
import { MyBetsMultiBetCardIndicatorComponent } from './components/multi-bet-card/my-bets-multi-bet-card-indicator.component';
import { MyBetsMultiBetCardComponent } from './components/multi-bet-card/my-bets-multi-bet-card.component';
import { MyBetsBetCardTitleComponent } from './components/my-bets-bet-card-title/my-bets-bet-card-title.component';
import { MyBetsBetslipScoreboardComponent } from './components/my-bets-betslip-scoreboard/my-bets-betslip-scoreboard.component';
import { MyBetsMigrationInfoComponent } from './components/my-bets-migration-tabs/my-bets-migration-info.component';
import { MyBetsMigrationTabsComponent } from './components/my-bets-migration-tabs/my-bets-migration-tabs.component';
import { MyBetsNavigationComponent } from './components/navigation/my-bets-navigation.component';
import { MyBetsPrintEditBetHistoryComponent } from './components/print/my-bets-print-edit-bet-history.component';
import { MyBetsPrintEditBetHistoryResolver } from './components/print/my-bets-print-edit-bet-history.resolver';
import { MyBetsPrintTitleComponent } from './components/print/my-bets-print-title.component';
import { MyBetsPrintComponent } from './components/print/my-bets-print.component';
import { MyBetsPrintResolver } from './components/print/my-bets-print.resolver';
import { MyBetsBetProtectorComponent } from './components/protector/my-bets-bet-protector.component';
import { BetslipConnectorService } from './components/re-bet/betslip-connector.service';
import { ReBetButtonComponent } from './components/re-bet/re-bet-button.component';
import { ReBetCalculatorService } from './components/re-bet/re-bet-calculator.service';
import { ReBetFiltersService } from './components/re-bet/re-bet-filters.service';
import { ReBetLoaderService } from './components/re-bet/re-bet-loader.service';
import { ReBetPicksService } from './components/re-bet/re-bet-picks.service';
import { ReBetTaxationService } from './components/re-bet/re-bet-taxation.service';
import { ReBetTrackingService } from './components/re-bet/re-bet-tracking.service';
import { ReBetAvailabilityService } from './components/re-bet/re-bet-visibility.service';
import { ReBetComponent } from './components/re-bet/re-bet.component';
import { MyBetsCondensedScoreboardComponent } from './components/scoreboard/general/my-bets-condensed-scoreboard.component';
import { MyBetsScoreboardWrapperComponent } from './components/scoreboard/my-bets-scoreboard-wrapper/my-bets-scoreboard-wrapper.component';
import { MyBetsScoreboardModule } from './components/scoreboard/my-bets-scoreboard.module';
import { MyBetsSingleBetCardComponent } from './components/single-bet-card/my-bets-single-bet-card.component';
import { StadiumMyBetsComponent } from './components/stadium-my-bets/stadium-my-bets.component';
import { MyBetsBetslipSummaryComponent } from './components/summary/my-bets-betslip-summary.component';
import { MyBetsSummaryComponent } from './components/summary/my-bets-summary.component';
import { TaxationInfoModule } from './components/taxation/taxation-info.module';
import { MyBetsPinComponent } from './components/toolbar/my-bets-pin.component';
import { MyBetsTaxinfoComponent } from './components/toolbar/my-bets-taxinfo.component';
import { MyBetsToolbarRedesignComponent } from './components/toolbar/my-bets-toolbar-redesign.component';
import { MyBetsToolbarComponent } from './components/toolbar/my-bets-toolbar.component';
import { MyBetsVisualizationComponent } from './components/visualization/my-bets-visualization.component';
import { MiniMyBetsViewComponent } from './mini-my-bets-view.component';
import { MyBetsBreadcrumbsResolver } from './my-bets-breadcrumbs.resolver';
import { MyBetsEventViewComponent } from './my-bets-event-view.component';
import { MyBetsRouteComponent } from './my-bets-route.component';
import { MyBetsRoutingModule } from './my-bets-routing.module';
import { MyBetsSubnavigationResolveService } from './my-bets-subnavigation/my-bets-subnavigation.service';
import { MyBetsViewComponent } from './my-bets-view.component';
import { MyBetsResolver } from './my-bets.resolver';
import { MyBetsDateFormatterPipe } from './pipes/my-bets-date-formatter.pipe';
import { MyBetsEventDateTimePipe } from './pipes/my-bets-event-date-time.pipe';
import { MyBetsEventDatePipe } from './pipes/my-bets-event-date.pipe';
import { MyBetsInputValuePipe } from './pipes/my-bets-input-value.pipe';
import { MyBetsMoneyPipe } from './pipes/my-bets-money.pipe';
import { AssociatedAccountService } from './services/associated-account-service';
import { AutoCashoutTrackingService } from './services/auto-cash-out-tracking-service';
import { AutoCashoutService } from './services/auto-cash-out.service';
import { BetslipPicksRequestBuilderService } from './services/betslip-pick-request-builder.service';
import { CashoutSettingsService } from './services/cash-out-settings.service';
import { EditMyBetButtonStateService } from './services/edit-my-bet-button-state.service';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';
import { MyBetsCondensedScoreboardService } from './services/my-bets-condensed-scoreboard.service';
import { MainMyBetsStrategy, MyBetsStrategy } from './services/my-bets-display.strategy';
import { MyBetsEarlypayoutButtonStateService } from './services/my-bets-earlypayout-button-state.service';
import { MyBetsFixtureDataService } from './services/my-bets-fixture-data.service';
import { MyBetsGuard } from './services/my-bets-guard';
import { MyBetsMigrationTabsService } from './services/my-bets-migration-tabs.service';
import { MyBetsPicksFactoryService } from './services/my-bets-picks-factory.service';
import { MyBetsPinningService } from './services/my-bets-pinning.service';
import { MyBetsTabsService } from './services/my-bets-tabs.service';
import { MyBetsVisualizationTrackingService } from './services/my-bets-visualization-tracking.service';
import { MyBetsVisualizationService } from './services/my-bets-visualization.service';
import { MyBetsSourcePageService } from './services/mybets-source-page.service';
import { PendingPayoutInfoService } from './services/pending-payout-info.service';
import { SettledBalanceService } from './services/settled-balance.service';
import { StadiumBetsVisibilityService } from './services/stadium-bets-visibility.service';

@NgModule({
    imports: [
        ArcModule,
        AutomationModule,
        RaceViewBestOddsBadgeComponent,
        CommonModule,
        CrmPostbetSignpostingModule,
        DirectivesModule,
        FormsModule,
        LoadingIndicatorModule,
        MyBetsBaseModule,
        MyBetsRoutingModule,
        MyBetsScoreboardModule,
        NativeAlertsModule,
        NavigationCoreModule,
        NgxPopperjsModule,
        NumpadModule,
        ShareMyBetModule,
        TimerModule,
        PriceboostCountSignpostComponent,
        TaxationInfoModule,
        DynamicLayoutSlotComponent,
        ShowInEpcotDirective,
        EpcotNotificationComponent,
        TrustPipe,
        HelpBoxBadgeComponent,
        HelpBoxComponent,
        MyBetsCondensedScoreboardComponent,
        MyBetsVisualizationComponent,
        DsButton,
        DsButtonIcon,
        IconCustomComponent,
        SportsThemedButtonDirective,
        MyBetsSummaryComponent,
        SGPBadgeComponent,
        SGPPlusBadgeComponent,
        MyBetsScoreboardWrapperComponent,
        DsDivider,
        CurrencyPipe,
        DsSegmentedControlModule,
        NgOptimizedImage,
        MyBetsSubNavigationComponent,
        MyBetsBetslipScoreboardComponent,
    ],
    declarations: [
        EditBetCashoutMessageComponent,
        HelpBoxArchivedComponent,
        HelpBoxReBetComponent,
        MiniMyBetsViewComponent,
        MyBetBetslipCardTitleOptionsComponent,
        MyBetsAutoCashoutBadgeComponent,
        MyBetsBetProtectorComponent,
        MyBetsBetSlipBadgesComponent,
        MyBetsBetbuilderCardComponent,
        MyBetsBetbuilderExpandedInfoComponent,
        MyBetsBetbuilderTitleComponent,
        MyBetsBetslipComponent,
        MyBetsBetslipEventInfoComponent,
        MyBetsBetslipExpandComponent,
        MyBetsBetslipMarketNameComponent,
        MyBetsBetslipNonEventInfoComponent,
        MyBetsBetslipOptionNameComponent,
        MyBetsBetslipSummaryComponent,
        MyBetsDateFormatterPipe,
        MyBetsEarlyPayoutAutocashoutButtonComponent,
        MyBetsEarlyPayoutAutocashoutComponent,
        MyBetsEarlyPayoutButtonComponent,
        MyBetsEarlyPayoutContainerComponent,
        MyBetsEarlyPayoutPopperTabsComponent,
        MyBetsEarlyPayoutRemoveAutocashoutButtonComponent,
        MyBetsEarlyPayoutRemoveAutocashoutComponent,
        MyBetsEarlyPayoutSettingsComponent,
        MyBetsEditBetButtonComponent,
        MyBetsEditBetHistoryBetslipComponent,
        MyBetsEditBetHistoryHeaderComponent,
        MyBetsEditBetHistoryListComponent,
        MyBetsEditedBadgeComponent,
        MyBetsEditingOverlayComponent,
        MyBetsEmptyListComponent,
        MyBetsEventDatePipe,
        MyBetsEventDateTimePipe,
        MyBetsEventViewComponent,
        MyBetsFilterButtonComponent,
        MyBetsFilterComponent,
        MyBetsFilterConstraintButtonComponent,
        MyBetsInputValuePipe,
        MyBetsListColumnComponent,
        MyBetsListComponent,
        MyBetsMigrationInfoComponent,
        MyBetsMigrationTabsComponent,
        MyBetsMultiBetCardCollapsedComponent,
        MyBetsMultiBetCardComponent,
        MyBetsMultiBetCardDetailComponent,
        MyBetsMultiBetCardIndicatorComponent,
        MyBetsNavigationComponent,
        MyBetsOutcomeBadgeComponent,
        MyBetsPinComponent,
        MyBetsPrintComponent,
        MyBetsPrintEditBetHistoryComponent,
        MyBetsPrintTitleComponent,
        ReBetComponent,
        MyBetsRouteComponent,
        MyBetsSettledFiltersComponent,
        MyBetsSingleBetCardComponent,
        MyBetsToolbarComponent,
        MyBetsToolbarRedesignComponent,
        MyBetsUsedRewardTokenComponent,
        MyBetsUsedRewardTokenEpcotComponent,
        MyBetsViewComponent,
        HelpBoxTransactionComponent,
        MyBetsMoneyPipe,
        ReBetButtonComponent,
        StadiumMyBetsComponent,
        MyBetsBetslipSportCompetitionNameComponent,
        MyBetsDetailsComponent,
        MyBetsTaxinfoComponent,
        MyBetsBetslipPayoutInfoComponent,
        MyBetsBetCardTitleComponent,
    ],
    exports: [MyBetsViewComponent, MyBetsEventViewComponent, MyBetsRouteComponent, MiniMyBetsViewComponent],
    providers: [
        MyBetsFixtureDataService,
        MyBetsBetslipRepository,
        MyBetsTabsService,
        MyBetsDateFormatterPipe,
        MyBetsPinningService,
        ActiveModal,
        AssociatedAccountService,
        AutoCashoutService,
        AutoCashoutTrackingService,
        BetslipPicksRequestBuilderService,
        CashoutSettingsService,
        MyBetsBetslipRepository,
        MyBetsBreadcrumbsResolver,
        MyBetsColumnsService,
        MyBetsDateFormatterPipe,
        MyBetsEarlyPayoutAutocashoutValidationService,
        MyBetsEarlypayoutButtonStateService,
        MyBetsFixtureDataService,
        MyBetsGuard,
        MyBetsListService,
        MyBetsMigrationTabsService,
        MyBetsPinningService,
        MyBetsPrintEditBetHistoryResolver,
        MyBetsPrintResolver,
        MyBetsResolver,
        MyBetsSourcePageService,
        MyBetsSubnavigationResolveService,
        MyBetsTabsService,
        MyBetsTabsService,
        ReBetAvailabilityService,
        ReBetFiltersService,
        ReBetLoaderService,
        ReBetPicksService,
        BetslipConnectorService,
        ReBetCalculatorService,
        ReBetTaxationService,
        ReBetTrackingService,
        SettledBalanceService,
        StadiumBetsVisibilityService,
        { provide: MyBetsStrategy, useClass: MainMyBetsStrategy },
        MyBetsMoneyPipe,
        EditMyBetButtonStateService,
        MyBetsDetailsResolver,
        MyBetsDetailsGuard,
        MyBetsDetailsSubNavigationService,
        MyBetsPicksFactoryService,
        BootstrapQuickLink,
        PendingPayoutInfoService,
        MyBetsVisualizationService,
        MyBetsCondensedScoreboardService,
        MyBetsListTrackingService,
        MyBetsVisualizationTrackingService,
    ],
})
export class MyBetsModule {
    readonly columnComponent = MiniMyBetsViewComponent;
    readonly eventComponent = MyBetsEventViewComponent;

    constructor(quickLinksBootstrap: BootstrapQuickLink) {
        quickLinksBootstrap.registerCountQuickLinks();
    }
}
